import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBStringUtils from "./DBStringUtils";
import DBCanvas from "./DBCanvas";

export default class DBFieldManager {

    public static OpenAddFieldWithType = (listId: string, type: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.EditingListId = listId;
            DBState.EditingFieldId = null;
            DBState.DraggedType = type;
            DBState.Options = ["Option 1"];

            const titleEl: HTMLElement | null = document.getElementById("fieldModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Add Column (" + type + ")"; }

            DBFieldManager.ClearFieldForm();
            DBFieldManager.SetChoiceLookupVisibility(type, listId);

            DBModalManager.OpenModal("fieldModal");
            const nameInput: HTMLElement | null = document.getElementById("fieldDisplayName");
            if (nameInput !== null) { nameInput.focus(); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.OpenAddFieldWithType");
        }
    };

    private static ClearFieldForm = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const displayName: HTMLInputElement | null = document.getElementById("fieldDisplayName") as HTMLInputElement;
            const internalName: HTMLInputElement | null = document.getElementById("fieldInternalName") as HTMLInputElement;
            const description: HTMLInputElement | null = document.getElementById("fieldDescription") as HTMLInputElement;
            if (displayName !== null) { displayName.value = ""; }
            if (internalName !== null) { internalName.value = ""; }
            if (description !== null) { description.value = ""; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ClearFieldForm");
        }
    };

    private static SetChoiceLookupVisibility = (type: string, listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const isChoice: boolean = type === "choice" || type === "multi-choice";
            const isLookup: boolean = type === "lookup";

            const choiceGroup: HTMLElement | null = document.getElementById("choiceOptionsGroup");
            const lookupGroup: HTMLElement | null = document.getElementById("lookupOptionsGroup");

            if (choiceGroup !== null) { choiceGroup.style.display = isChoice ? "block" : "none"; }
            if (lookupGroup !== null) { lookupGroup.style.display = isLookup ? "block" : "none"; }

            if (isChoice) { DBFieldManager.RenderOptions(); }
            if (isLookup) {
                DBFieldManager.PopulateLookupLists();
                if (DBState.Lists.length === 1) {
                    DBModalManager.ShowToast("Create another list first to use lookup columns", "error");
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.SetChoiceLookupVisibility");
        }
    };

    public static EditField = (listId: string, internalName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            const field: IFieldDefinition = list.Properties[internalName];
            if (!field) { return; }

            DBState.EditingListId = listId;
            DBState.EditingFieldId = internalName;
            DBState.DraggedType = DBFieldManager.DetermineFieldType(field);

            const titleEl: HTMLElement | null = document.getElementById("fieldModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Edit Column (" + DBState.DraggedType + ")"; }

            DBFieldManager.PopulateFieldForm(field, internalName);
            DBFieldManager.SetChoiceLookupVisibility(DBState.DraggedType, listId);

            if (DBState.DraggedType === "choice" || DBState.DraggedType === "multi-choice") {
                DBFieldManager.LoadExistingOptions(field);
            }
            if (DBState.DraggedType === "lookup") {
                DBFieldManager.SetLookupListValue(field);
            }

            DBModalManager.OpenModal("fieldModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.EditField");
        }
    };

    private static DetermineFieldType = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Format === "lookup") { return "lookup"; }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum) { return "multi-choice"; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "choice"; }
            if (field.Format) { return field.Format; }
            if (field.Type === "integer") { return "integer"; }
            if (field.Type === "number") { return "number"; }
            if (field.Type === "boolean") { return "boolean"; }
            if (field.Type === "string") { return "text"; }
            return field.Type || "text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.DetermineFieldType");
            return "";
        }
    };

    private static PopulateFieldForm = (field: IFieldDefinition, internalName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const displayName: HTMLInputElement | null = document.getElementById("fieldDisplayName") as HTMLInputElement;
            const internalNameInput: HTMLInputElement | null = document.getElementById("fieldInternalName") as HTMLInputElement;
            const description: HTMLInputElement | null = document.getElementById("fieldDescription") as HTMLInputElement;
            if (displayName !== null) { displayName.value = field.Title || ""; }
            if (internalNameInput !== null) { internalNameInput.value = internalName; }
            if (description !== null) { description.value = field.Description || ""; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.PopulateFieldForm");
        }
    };

    private static LoadExistingOptions = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) {
                DBState.Options = field.Enum.slice();
            } else if (field.Items !== null && field.Items !== undefined && field.Items.Enum !== null && field.Items.Enum.length > 0) {
                DBState.Options = field.Items.Enum.slice();
            } else {
                DBState.Options = ["Option 1"];
            }
            DBFieldManager.RenderOptions();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.LoadExistingOptions");
        }
    };

    private static SetLookupListValue = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBFieldManager.PopulateLookupLists();
            const SetValue = (): void => {
                if (field.LookupList) {
                    const select: HTMLSelectElement | null = document.getElementById("lookupListSelect") as HTMLSelectElement;
                    if (select !== null) { select.value = field.LookupList; }
                }
            };
            setTimeout(SetValue, 10);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.SetLookupListValue");
        }
    };

    public static SaveField = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const displayName: string = (document.getElementById("fieldDisplayName") as HTMLInputElement).value.trim();
            const internalName: string = (document.getElementById("fieldInternalName") as HTMLInputElement).value.trim();
            const description: string = (document.getElementById("fieldDescription") as HTMLInputElement).value.trim();
            const type: string = DBState.DraggedType || "text";

            if (!DBFieldManager.ValidateSaveInput(displayName, internalName)) { return; }

            const list: IListDefinition | null = DBState.FindList(DBState.EditingListId || "");
            if (list === null) { return; }

            const field: any = { Title: displayName, Description: description };
            const built: boolean = DBFieldManager.BuildFieldByType(field, type);
            if (!built) { return; }

            DBFieldManager.ApplyFieldConstraints(field);
            DBFieldManager.CommitField(list, internalName, field);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.SaveField");
        }
    };

    private static ValidateSaveInput = (displayName: string, internalName: string): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (!displayName) {
                DBModalManager.ShowToast("Display name is required", "error");
                return false;
            }
            if (!internalName) {
                DBModalManager.ShowToast("Internal name is required", "error");
                return false;
            }
            const validation: { valid: boolean; error?: string } = DBFieldManager.ValidateFieldName(internalName);
            if (!validation.valid) {
                DBModalManager.ShowToast(validation.error || "Invalid field name", "error");
                return false;
            }
            if (DBFieldManager.CheckDuplicateField(DBState.EditingListId || "", internalName, DBState.EditingFieldId)) {
                DBModalManager.ShowToast("A column with this internal name already exists", "error");
                return false;
            }
            return true;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateSaveInput");
            return false;
        }
    };

    private static BuildFieldByType = (field: any, type: string): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            switch (type) {
                case "text": field.Type = "string"; break;
                case "note": field.Type = "string"; field.Format = "note"; break;
                case "rich-text": field.Type = "string"; field.Format = "rich-text"; break;
                case "integer": field.Type = "integer"; break;
                case "number": field.Type = "number"; break;
                case "currency": field.Type = "number"; field.Format = "currency"; break;
                case "boolean": field.Type = "boolean"; break;
                case "date-time": field.Type = "string"; field.Format = "date-time"; break;
                case "choice": field.Type = "string"; field.Enum = DBFieldManager.FilterOptions(); break;
                case "multi-choice": field.Type = "array"; field.Items = { Enum: DBFieldManager.FilterOptions() }; break;
                case "lookup": return DBFieldManager.BuildLookupField(field);
                case "url": field.Type = "string"; field.Format = "url"; break;
                case "user": field.Type = "string"; field.Format = "user"; break;
                case "geolocation": field.Type = "string"; field.Format = "geolocation"; break;
                default:
                    DBModalManager.ShowToast("Unknown column type: " + type, "error");
                    return false;
            }
            return true;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.BuildFieldByType");
            return false;
        }
    };

    private static FilterOptions = (): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const HasContent = (o: string): boolean => { return o.trim() !== ""; };
            return DBState.Options.filter(HasContent);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.FilterOptions");
            return [];
        }
    };

    private static BuildLookupField = (field: any): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            const select: HTMLSelectElement | null = document.getElementById("lookupListSelect") as HTMLSelectElement;
            const lookupList: string = select !== null ? select.value : "";
            if (!lookupList) {
                DBModalManager.ShowToast("Please select a target list", "error");
                return false;
            }

            const currentList: IListDefinition | null = DBState.FindList(DBState.EditingListId || "");
            if (currentList !== null && lookupList === currentList.Title) {
                DBModalManager.ShowToast("Cannot create a lookup to the same list", "error");
                return false;
            }

            const FindByTitle = (l: IListDefinition): boolean => { return l.Title === lookupList; };
            const targetExists: boolean = DBState.Lists.some(FindByTitle);
            if (!targetExists) {
                DBModalManager.ShowToast("Target list no longer exists", "error");
                return false;
            }

            field.Type = "string";
            field.Format = "lookup";
            field.LookupList = lookupList;
            field.LookupField = "Title";
            return true;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.BuildLookupField");
            return false;
        }
    };

    private static ApplyFieldConstraints = (field: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const required: boolean = (document.getElementById("fieldRequired") as HTMLInputElement).checked;
            const minLength: number = parseInt((document.getElementById("fieldMinLength") as HTMLInputElement).value) || 0;
            const maxLength: number = parseInt((document.getElementById("fieldMaxLength") as HTMLInputElement).value) || 0;
            const defaultValue: string = (document.getElementById("fieldDefaultValue") as HTMLInputElement).value.trim();

            if (required) { field.Required = true; }
            if (minLength > 0) { field.MinLength = minLength; }
            if (maxLength > 0) { field.MaxLength = maxLength; }
            if (defaultValue) { field.DefaultValue = defaultValue; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ApplyFieldConstraints");
        }
    };

    private static CommitField = (list: IListDefinition, internalName: string, field: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.EditingFieldId && DBState.EditingFieldId !== internalName) {
                delete list.Properties[DBState.EditingFieldId];
            }

            list.Properties[internalName] = field;
            DBState.SaveState();
            DBFieldManager.RenderFields(list);
            DBModalManager.CloseModal("fieldModal");
            DBModalManager.ShowToast(DBState.EditingFieldId ? "Column updated" : "Column added", "success");

            if (field.Format === "lookup") {
                const DrawLater = (): void => { DBCanvas.DrawRelationships(); };
                setTimeout(DrawLater, 100);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.CommitField");
        }
    };

    public static DeleteField = (listId: string, internalName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (!confirm("Delete this column?")) { return; }
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            const wasLookup: boolean = list.Properties[internalName] !== undefined && list.Properties[internalName].Format === "lookup";
            delete list.Properties[internalName];

            DBFieldManager.CleanupViewReferences(list, internalName);
            DBState.SaveState();
            DBFieldManager.RenderFields(list);
            DBModalManager.ShowToast("Column deleted", "success");

            if (wasLookup) {
                const DrawLater = (): void => { DBCanvas.DrawRelationships(); };
                setTimeout(DrawLater, 100);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.DeleteField");
        }
    };

    private static CleanupViewReferences = (list: IListDefinition, internalName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (list.Views === null || list.Views === undefined) { return; }
            const CleanView = (view: any): void => {
                const KeepField = (f: string): boolean => { return f !== internalName; };
                const KeepAgg = (a: any): boolean => { return a.FieldName !== internalName; };
                view.ViewFields = (view.ViewFields || []).filter(KeepField);
                view.Aggregations = (view.Aggregations || []).filter(KeepAgg);
            };
            list.Views.forEach(CleanView);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.CleanupViewReferences");
        }
    };

    public static RenderFields = (list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("fieldList_" + list.Id);
            if (container === null) { return; }
            container.innerHTML = "";

            const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            const RenderOne = (entry: [string, IFieldDefinition]): void => {
                DBFieldManager.RenderSingleField(container, list, entry[0], entry[1]);
            };
            entries.forEach(RenderOne);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.RenderFields");
        }
    };

    private static RenderSingleField = (container: HTMLElement, list: IListDefinition, internalName: string, field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldEl: HTMLDivElement = document.createElement("div");
            const isLookup: boolean = field.Format === "lookup";
            fieldEl.className = "field-item" + (isLookup ? " lookup-field" : "");

            const typeLabel: string = DBFieldManager.GetTypeLabel(field);
            const icon: string = DBFieldManager.GetFieldIcon(field);
            const iconClass: string = DBFieldManager.GetFieldIconClass(field);
            const lookupInfo: string = DBFieldManager.GetLookupInfo(field);
            const constraintsBadges: string = DBFieldManager.GetConstraintBadges(field);
            const settingsIndicators: string = DBFieldManager.GetSettingsIndicators(field);
            const eid: string = DBStringUtils.EscapeHtml(list.Id);
            const efn: string = DBStringUtils.EscapeHtml(internalName);

            fieldEl.innerHTML = '<div class="field-type-icon ' + iconClass + '">' + icon + '</div>'
                + '<div class="field-info">'
                + '<div class="field-display-name">' + DBStringUtils.EscapeHtml(field.Title) + settingsIndicators + (field.Description ? " \u2022 " + DBStringUtils.EscapeHtml(field.Description) : "") + lookupInfo + constraintsBadges + '</div>'
                + '<div class="field-internal-name">' + efn + '</div>'
                + '</div>'
                + '<div class="field-type-badge">' + DBStringUtils.EscapeHtml(typeLabel) + '</div>'
                + '<button class="field-delete-btn" data-action="open-field-settings" data-list-id="' + eid + '" data-field-name="' + efn + '" title="Field Settings" style="font-size: 11px;">\u2699</button>'
                + '<button class="field-delete-btn" data-action="delete-field" data-list-id="' + eid + '" data-field-name="' + efn + '">\u00D7</button>';

            const HandleClick = (e: Event): void => {
                const target: HTMLElement = e.target as HTMLElement;
                if (!target.closest(".field-delete-btn") && !target.closest("[data-action]")) {
                    DBFieldManager.EditField(list.Id, internalName);
                }
            };
            fieldEl.addEventListener("click", HandleClick);
            container.appendChild(fieldEl);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.RenderSingleField");
        }
    };

    private static GetTypeLabel = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum) { return "multi-choice"; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "choice"; }
            if (field.Format) { return field.Format; }
            return field.Type || "string";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetTypeLabel");
            return "";
        }
    };

    public static GetFieldIcon = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Format === "lookup") { return "\u2197"; }
            if (field.Format === "user") { return "\uD83D\uDC64"; }
            if (field.Format === "url") { return "\uD83D\uDD17"; }
            if (field.Format === "currency") { return "\u00A3"; }
            if (field.Format === "date-time") { return "\uD83D\uDCC5"; }
            if (field.Format === "note") { return "\u00B6"; }
            if (field.Format === "rich-text") { return "RT"; }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum) { return "\u2611"; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "\u25BC"; }
            if (field.Type === "boolean") { return "\u2713"; }
            if (field.Type === "integer") { return "123"; }
            if (field.Type === "number") { return "1.5"; }
            if (field.Type === "string") { return "Ab"; }
            return "Ab";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetFieldIcon");
            return "";
        }
    };

    public static GetFieldIconClass = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Format === "lookup" || field.Format === "user") { return "type-lookup"; }
            if (field.Format === "url") { return "type-url"; }
            if (field.Format === "currency") { return "type-currency"; }
            if (field.Format === "date-time") { return "type-date"; }
            if (field.Format === "note" || field.Format === "rich-text") { return "type-text"; }
            if (field.Items !== null && field.Items !== undefined && field.Items.Enum) { return "type-choice"; }
            if (field.Enum !== null && field.Enum !== undefined && field.Enum.length > 0) { return "type-choice"; }
            if (field.Type === "boolean") { return "type-boolean"; }
            if (field.Type === "integer") { return "type-integer"; }
            if (field.Type === "number") { return "type-number"; }
            if (field.Type === "string") { return "type-text"; }
            return "type-text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetFieldIconClass");
            return "";
        }
    };

    private static GetLookupInfo = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (field.Format === "lookup" && field.LookupList) {
                return '<div style="font-size: 10px; color: #3b82f6; margin-top: 2px;">\u2192 ' + DBStringUtils.EscapeHtml(field.LookupList) + '</div>';
            }
            return "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetLookupInfo");
            return "";
        }
    };

    private static GetConstraintBadges = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (!field.Required && !field.MinLength && !field.MaxLength && !field.DefaultValue) { return ""; }
            const badges: string[] = [];
            if (field.Required) { badges.push('<span class="constraint-badge required">Required</span>'); }
            if (field.MinLength) { badges.push('<span class="constraint-badge">Min ' + field.MinLength + '</span>'); }
            if (field.MaxLength) { badges.push('<span class="constraint-badge">Max ' + field.MaxLength + '</span>'); }
            if (field.DefaultValue) { badges.push('<span class="constraint-badge default">Default: "' + DBStringUtils.EscapeHtml(field.DefaultValue) + '"</span>'); }
            return '<div style="margin-top: 4px; display: flex; gap: 4px; flex-wrap: wrap;">' + badges.join("") + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetConstraintBadges");
            return "";
        }
    };

    private static GetSettingsIndicators = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const indicators: string[] = [];
            if (field.ShowInDisplayForm === false) { indicators.push("D"); }
            if (field.ShowInEditForm === false) { indicators.push("E"); }
            if (field.ShowInNewForm === false) { indicators.push("N"); }
            if (field.CustomFormatter) { indicators.push("F"); }
            if (field.ValidationFormula) { indicators.push("V"); }
            if (field.EnforceUniqueValues) { indicators.push("U"); }
            if (indicators.length === 0) { return ""; }
            return '<span style="font-size: 9px; padding: 1px 4px; background: #dbeafe; color: #1e40af; border-radius: 3px; margin-left: 4px;" title="Hidden from forms: ' + indicators.join(",") + '">\u2699' + indicators.join("") + '</span>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.GetSettingsIndicators");
            return "";
        }
    };

    public static RenderOptions = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("optionsList");
            if (container === null) { return; }
            container.innerHTML = "";

            const RenderOneOption = (opt: string, index: number): void => {
                const div: HTMLDivElement = document.createElement("div");
                div.className = "option-item";
                div.innerHTML = '<input type="text" class="form-input option-input" value="' + opt + '" data-option-index="' + index + '">'
                    + '<button class="option-remove-btn" data-option-remove="' + index + '">\u00D7</button>';
                container.appendChild(div);
            };
            DBState.Options.forEach(RenderOneOption);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.RenderOptions");
        }
    };

    public static AddOption = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.Options.push("Option " + (DBState.Options.length + 1));
            DBFieldManager.RenderOptions();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.AddOption");
        }
    };

    public static UpdateOption = (index: number, value: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.Options[index] = value;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.UpdateOption");
        }
    };

    public static RemoveOption = (index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.Options.length > 1) {
                DBState.Options.splice(index, 1);
                DBFieldManager.RenderOptions();
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.RemoveOption");
        }
    };

    public static PopulateLookupLists = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const select: HTMLSelectElement | null = document.getElementById("lookupListSelect") as HTMLSelectElement;
            if (select === null) { return; }
            select.innerHTML = '<option value="">Select a list...</option>';

            const AddListOption = (list: IListDefinition): void => {
                if (list.Id === DBState.EditingListId) { return; }
                const option: HTMLOptionElement = document.createElement("option");
                option.value = list.Title;
                option.textContent = list.Title;
                select.appendChild(option);
            };
            DBState.Lists.forEach(AddListOption);

            if (DBState.Lists.length === 1) {
                const placeholder: HTMLOptionElement = document.createElement("option");
                placeholder.value = "";
                placeholder.textContent = "(Create another list first)";
                placeholder.disabled = true;
                select.appendChild(placeholder);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.PopulateLookupLists");
        }
    };

    public static ValidateFieldName = (name: string): { valid: boolean; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            if (!name || name.trim() === "") {
                return { valid: false, error: "Column name cannot be empty" };
            }
            if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(name)) {
                return { valid: false, error: "Column name must start with a letter and contain only letters and numbers" };
            }
            return { valid: true };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateFieldName");
            return { valid: false };
        }
    };

    public static CheckDuplicateField = (listId: string, fieldName: string, excludeField: string | null): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return false; }

            const IsDuplicate = (key: string): boolean => {
                return key === fieldName && key !== excludeField;
            };
            return Object.keys(list.Properties || {}).some(IsDuplicate);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.CheckDuplicateField");
            return false;
        }
    };

    public static ValidateCellValue = (value: string, column: any): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            if (column.Required && (!value || value === "")) {
                return { valid: false, error: column.Title + " is required" };
            }
            if (!value || value === "") {
                return { valid: true, value: column.DefaultValue || "" };
            }
            return DBFieldManager.ValidateByType(value, column);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateCellValue");
            return { valid: false };
        }
    };

    private static ValidateByType = (value: string, column: any): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            if (column.Type === "number" || column.Format === "number" || column.Format === "integer") {
                if (isNaN(Number(value))) {
                    return { valid: false, error: '"' + value + '" is not a valid number for ' + column.Title };
                }
                return { valid: true, value: value };
            }
            if (column.Format === "boolean" || column.Type === "boolean") {
                return DBFieldManager.ValidateBooleanValue(value, column.Title);
            }
            if (column.Format === "date" || column.Format === "date-time") {
                return DBFieldManager.ValidateDateValue(value, column.Title);
            }
            if (column.Format === "url") {
                return DBFieldManager.ValidateUrlValue(value, column.Title);
            }
            if (column.Type === "string" || !column.Type) {
                return DBFieldManager.ValidateStringLength(value, column);
            }
            return { valid: true, value: value };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateByType");
            return { valid: false };
        }
    };

    private static ValidateBooleanValue = (value: string, title: string): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            const lv: string = value.toLowerCase();
            if (lv !== "true" && lv !== "false" && lv !== "1" && lv !== "0" && lv !== "yes" && lv !== "no") {
                return { valid: false, error: '"' + value + '" is not a valid boolean for ' + title + '. Use: true, false, yes, no, 1, or 0' };
            }
            const normalized: string = (lv === "true" || lv === "1" || lv === "yes") ? "true" : "false";
            return { valid: true, value: normalized };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateBooleanValue");
            return { valid: false };
        }
    };

    private static ValidateDateValue = (value: string, title: string): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            const datePattern: RegExp = /^\d{4}-\d{2}-\d{2}$/;
            if (!datePattern.test(value)) {
                return { valid: false, error: '"' + value + '" is not a valid date for ' + title + '. Use format: YYYY-MM-DD' };
            }
            const date: Date = new Date(value);
            if (isNaN(date.getTime())) {
                return { valid: false, error: '"' + value + '" is not a valid date for ' + title };
            }
            return { valid: true, value: value };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateDateValue");
            return { valid: false };
        }
    };

    private static ValidateUrlValue = (value: string, title: string): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            new URL(value);
            return { valid: true, value: value };
        } catch (e) {
            return { valid: false, error: '"' + value + '" is not a valid URL for ' + title + '. URLs must start with http:// or https://' };
        }
    };

    private static ValidateStringLength = (value: string, column: any): { valid: boolean; value?: string; error?: string } => {
        if (ks.kErrorMode === true) { return { valid: false }; }
        try {
            if (column.MinLength && value.length < column.MinLength) {
                return { valid: false, error: column.Title + " must be at least " + column.MinLength + " characters (currently " + value.length + ")" };
            }
            if (column.MaxLength && value.length > column.MaxLength) {
                return { valid: false, error: column.Title + " cannot exceed " + column.MaxLength + " characters (currently " + value.length + ")" };
            }
            return { valid: true, value: value };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBFieldManager.ValidateStringLength");
            return { valid: false };
        }
    };
}
